import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { createCheckout } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/utils";

export default function Checkout() {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { items, subtotal } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ customerName: "", customerEmail: "", customerPhone: "", address: "", city: "", postalCode: "", country: "", notes: "" });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!items.length) return;
    setSubmitting(true);
    try {
      const shippingAddress = `${form.address}, ${form.postalCode} ${form.city}, ${form.country}`;
      const result = await createCheckout({
        items: items.map((item) => ({ artworkId: item.id, quantity: item.quantity })),
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone || undefined,
        shippingAddress,
        notes: form.notes || undefined,
        locale,
      });
      if (result.url) window.location.assign(result.url);
      else if (result.orderId) navigate(`/order-confirmation?id=${result.orderId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.common.error);
      setSubmitting(false);
    }
  };

  if (!items.length) return (
    <div className="checkout-page"><div className="checkout-empty">
      <ShoppingBag size={36} /><h1>{t.cart.empty}</h1>
      <Link to="/gallery">{t.cart.continueShopping}</Link>
    </div></div>
  );

  return (
    <div className="checkout-page">
      <button onClick={() => navigate(-1)} className="checkout-back"><ArrowLeft size={15} />{t.common.cancel}</button>
      <div className="checkout-layout container">
        <form onSubmit={handleSubmit} className="checkout-form">
          <span className="gallery-index">Dalila Di Maria · Secure checkout</span>
          <h1>{t.checkout.title}</h1>
          <div className="checkout-fields two-columns">
            <label>{t.checkout.name} *<input required autoComplete="name" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
            <label>{t.checkout.email} *<input required type="email" autoComplete="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} /></label>
          </div>
          <div className="checkout-fields">
            <label>{t.checkout.phone}<input type="tel" autoComplete="tel" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} /></label>
            <label>{t.checkout.address} *<input required autoComplete="street-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
          </div>
          <div className="checkout-fields three-columns">
            <label>{t.checkout.city} *<input required autoComplete="address-level2" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
            <label>{t.checkout.postalCode} *<input required autoComplete="postal-code" value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></label>
            <label>{t.checkout.country} *<input required autoComplete="country-name" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></label>
          </div>
          <label className="checkout-notes">{t.checkout.notes}<textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <button type="submit" disabled={submitting} className="checkout-pay">
            {submitting ? t.checkout.processing : t.checkout.pay}<Lock size={15} />
          </button>
          <p className="checkout-secure"><Lock size={12} />{t.checkout.secure}</p>
        </form>

        <aside className="checkout-summary">
          <span className="gallery-index">{t.cart.title}</span>
          <div className="checkout-summary-items">{items.map((item) => (
            <article key={item.id}>
              <img src={item.image} alt={item.title} />
              <div><span>{item.category}</span><h2>{item.title}</h2><p>{item.quantity} × {formatPrice(item.price, item.currency)}</p></div>
            </article>
          ))}</div>
          <div className="checkout-total"><span>{t.cart.total}</span><strong>{formatPrice(subtotal, items[0].currency)}</strong></div>
        </aside>
      </div>
    </div>
  );
}
